import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { CreditCard, Lock, AlertCircle, CheckCircle, Shield } from 'lucide-react';
import axios from 'axios';
import { formatStripeError } from '../../lib/stripeErrors';
import { InAppBrowserWarning } from '../InAppBrowserWarning';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      '::placeholder': { color: '#6b7280' },
      iconColor: '#a855f7',
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
};

const CaptureForm = ({ bookingIds, clientSecret, onSaved }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret || processing) return;
    setProcessing(true);
    setError(null);
    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (stripeError) {
        setError(formatStripeError(stripeError));
        return;
      }
      if (setupIntent.status === 'succeeded') {
        const res = await axios.post(`${API_URL}/api/bootcamps/bookings/save-card`, {
          booking_ids: bookingIds,
          setup_intent_id: setupIntent.id,
        });
        onSaved(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save card — please try again');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <InAppBrowserWarning />
      <div className="p-4 bg-neutral-800 rounded-lg border border-neutral-700">
        <CardElement options={cardElementOptions} />
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2" data-testid="card-capture-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 text-white/40 text-xs">
        <Lock className="w-3 h-3" />
        <span>Card details are stored securely by Stripe — nothing is charged now</span>
      </div>

      <Button
        type="submit"
        disabled={!stripe || !clientSecret || processing}
        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 h-12"
        data-testid="save-card-btn"
      >
        {processing ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            Saving card...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Save Card & Secure Place
          </>
        )}
      </Button>
    </form>
  );
};

const BootcampCardCaptureModal = ({ bookings, bootcamp, open, onClose, onDone, onPayNow }) => {
  const bookingIds = (bookings || []).map((b) => b.id);
  const [clientSecret, setClientSecret] = useState(null);
  const [savedCard, setSavedCard] = useState(null);
  const totalAmount = (bookings || []).reduce((acc, b) => acc + Number(b.amount || 0), 0);
  const studentNames = (bookings || []).map((b) => b.student_name).filter(Boolean).join(' & ');

  useEffect(() => {
    if (!open || !bookingIds.length) return;
    setSavedCard(null);
    setClientSecret(null);
    axios
      .post(`${API_URL}/api/installments/create-setup-intent-multi?bootcamp_booking_ids=${bookingIds.join(',')}`)
      .then((res) => setClientSecret(res.data.client_secret))
      .catch((err) => toast.error(err.response?.data?.detail || 'Could not start card setup'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSaved = (data) => {
    setSavedCard(data);
    toast.success('Card saved — place secured!');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md bg-neutral-900 border-neutral-800 text-white" data-testid="card-capture-modal">
        {!savedCard ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-400" />
                Secure Your Place
              </DialogTitle>
            </DialogHeader>
            <p className="text-white/70 text-sm">
              Add your card to secure {studentNames ? <strong>{studentNames}'s</strong> : 'your'} place on{' '}
              <strong>{bootcamp?.name}</strong>. Your card is saved securely —{' '}
              <strong>nothing is charged right now</strong>. We'll confirm the booking and take the payment of{' '}
              <strong>£{totalAmount.toFixed(2)}</strong>.
            </p>
            <Elements stripe={stripePromise}>
              <CaptureForm bookingIds={bookingIds} clientSecret={clientSecret} onSaved={handleSaved} />
            </Elements>
            {onPayNow && (
              <button
                type="button"
                onClick={onPayNow}
                className="text-purple-400 hover:text-purple-300 text-sm underline text-center w-full"
                data-testid="pay-now-instead-btn"
              >
                Prefer to pay now? Pay in full or set up instalments
              </button>
            )}
          </>
        ) : (
          <div className="text-center space-y-4 py-4" data-testid="card-saved-confirmation">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle className="w-9 h-9 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white">Place Secured!</h3>
            <p className="text-white/70 text-sm">
              Your card{' '}
              {savedCard.card_last4 && (
                <strong>{(savedCard.card_brand || 'card').toUpperCase()} •••• {savedCard.card_last4}</strong>
              )}{' '}
              has been saved. We'll be in touch to confirm the booking and take the payment of{' '}
              <strong>£{totalAmount.toFixed(2)}</strong>.
            </p>
            <div className="flex flex-col gap-2">
              {onPayNow && (
                <Button
                  variant="outline"
                  onClick={onPayNow}
                  className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
                  data-testid="pay-now-after-save-btn"
                >
                  Prefer to pay now? Pay in full or instalments
                </Button>
              )}
              <Button
                onClick={onDone}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                data-testid="card-capture-done-btn"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BootcampCardCaptureModal;
